import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import type { Api } from '../api.js';
import { useLang, type Lang } from '../i18n.js';
import { navigate } from '../router.js';
import { Avatar, Block, LoadError, Loading, Pill } from '../ui.js';

/**
 * The project's Activity tab (owner 2026-11-01: "Den här designen ser inte bra ut"). A timeline that reads left to right:
 * time · the actor's ball (name and role on hover) · what happened. Grouped by day; what one person did the same minute to
 * the same thing (a casting role, the videos) is one entry. Videos, people and roles are links (owner: "kan man också
 * klicka på de olika delarna").
 */

type Stage = 'yes' | 'no' | 'asked' | 'booked' | 'passed' | 'shortlist';
type Ref = { id: string | null; name: string };
export type ActivityRow = {
  at: string;
  actor: string | null;
  actorRole?: string | null;
  system?: boolean;
  text: string;
  kind: 'step' | 'casting' | 'change';
  video?: Ref;
  from?: { stage: string; label: string } | null;
  to?: { stage: string; label: string };
  note?: string[];
  casting?: {
    verb: 'role_added' | 'role_changed' | 'role_removed' | 'candidates_added' | 'stage' | 'candidate_removed' | 'booked' | 'asked' | 'answered';
    role: Ref | null;
    people: Ref[];
    status: Stage | null;
    count?: number;
    emailed?: number | null;
    tape?: boolean;
  };
};
type Filter = 'all' | 'step' | 'casting' | 'change';

const TX = {
  en: {
    activity: 'Activity',
    nothingYet: 'Nothing yet.',
    show: 'Show',
    filters: { all: 'All', step: 'Steps', casting: 'Casting', change: 'Other' } as Record<Filter, string>,
    today: 'Today',
    yesterday: 'Yesterday',
    changes: (n: number) => `${n} changes`,
    older: 'Show older',
    casting: 'Casting',
    status: { shortlist: 'Candidate', asked: 'Asked', yes: 'Yes', no: 'No', booked: 'Booked', passed: 'Not chosen' } as Record<Stage, string>,
    and: 'and',
    aCandidate: 'A candidate',
    roleAdded: 'Created the role',
    roleChanged: 'Changed the role',
    roleRemoved: 'Removed the role',
    added: (n: number) => (n === 1 ? 'as a candidate' : 'as candidates'),
    add: 'Added',
    tookOff: 'Took off the role:',
    booked: 'Booked',
    answered: 'answered',
    tape: 'with a self-tape',
    asked: (n: number) => `Asked ${n} ${n === 1 ? 'person' : 'people'}`,
    emailed: (n: number) => `${n} emailed from hub@`,
    copied: 'links copied',
    openVideo: (v: string) => `Open ${v}`,
    openPerson: (v: string) => `${v} in the actor library`,
    openRole: (v: string) => `The role ${v} under Actors`
  },
  sv: {
    activity: 'Aktivitet',
    nothingYet: 'Inget än.',
    show: 'Visa',
    filters: { all: 'Allt', step: 'Steg', casting: 'Casting', change: 'Övrigt' } as Record<Filter, string>,
    today: 'I dag',
    yesterday: 'I går',
    changes: (n: number) => `${n} ändringar`,
    older: 'Visa äldre',
    casting: 'Casting',
    status: { shortlist: 'Kandidat', asked: 'Tillfrågad', yes: 'Ja', no: 'Nej', booked: 'Bokad', passed: 'Inte vald' } as Record<Stage, string>,
    and: 'och',
    aCandidate: 'En kandidat',
    roleAdded: 'Skapade rollen',
    roleChanged: 'Ändrade rollen',
    roleRemoved: 'Tog bort rollen',
    added: (n: number) => (n === 1 ? 'som kandidat' : 'som kandidater'),
    add: 'Lade till',
    tookOff: 'Tog bort från rollen:',
    booked: 'Bokade',
    answered: 'svarade',
    tape: 'med self-tape',
    asked: (n: number) => `Frågade ${n} ${n === 1 ? 'person' : 'personer'}`,
    emailed: (n: number) => `${n} mejlade från hub@`,
    copied: 'länkar kopierade',
    openVideo: (v: string) => `Öppna ${v}`,
    openPerson: (v: string) => `${v} i skådespelarbiblioteket`,
    openRole: (v: string) => `Rollen ${v} under Skådespelare`
  }
};

const TONE: Record<Stage, 'ok' | 'warn' | 'info' | 'neutral'> = { shortlist: 'neutral', asked: 'info', yes: 'ok', no: 'warn', booked: 'ok', passed: 'neutral' };
const STEP_CLASS: Record<string, string> = { planning: 'planning', editing: 'editing', internal_review: 'internal_review', with_customer: 'with_customer', ready_to_deliver: 'ready_to_deliver', delivered: 'delivered' };

const dayKey = (iso: string) => new Date(iso).toLocaleDateString('sv-SE', { timeZone: 'Europe/Stockholm' });
const clock = (iso: string) => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Stockholm', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));

/** "Today", "Yesterday", or the weekday and date ("Tue 27 Oct"; the year only when it is not this year). */
export function dayLabel(key: string, now: Date, lang: Lang): string {
  const t = TX[lang];
  const today = dayKey(now.toISOString());
  if (key === today) return t.today;
  if (key === dayKey(new Date(now.getTime() - 86400_000).toISOString())) return t.yesterday;
  const d = new Date(`${key}T12:00:00Z`);
  const s = new Intl.DateTimeFormat(lang === 'sv' ? 'sv-SE' : 'en-GB', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short', ...(key.slice(0, 4) !== today.slice(0, 4) ? { year: 'numeric' } : {}) }).format(d);
  return s.replace(/\./g, '').replace(/^\w/, (c) => c.toUpperCase());
}

/** What a row is about, for folding: the casting role, or the videos' steps, or the other changes. */
const subject = (r: ActivityRow) => (r.kind === 'casting' ? `casting:${r.casting?.role?.id ?? r.casting?.role?.name ?? ''}` : r.kind);

export type Entry = { at: string; actor: string | null; actorRole: string | null; system: boolean; rows: ActivityRow[] };
export type Day = { key: string; entries: Entry[] };

/** Newest first, by day; one person's rows about the same thing in the same minute become one entry. */
export function groupActivity(rows: ActivityRow[]): Day[] {
  const days: Day[] = [];
  for (const r of rows) {
    const key = dayKey(r.at);
    let day = days[days.length - 1];
    if (!day || day.key !== key) days.push((day = { key, entries: [] }));
    const last = day.entries[day.entries.length - 1];
    if (last && last.actor === r.actor && clock(last.at) === clock(r.at) && subject(last.rows[0]!) === subject(r)) last.rows.push(r);
    else day.entries.push({ at: r.at, actor: r.actor, actorRole: r.actorRole ?? null, system: Boolean(r.system), rows: [r] });
  }
  return days;
}

const PAGE = 40;
const go = (path: string) => (e: Event) => (e.preventDefault(), navigate(path));

export function ProjectActivity(p: { api: Api; id: string; now?: Date }) {
  const lang = useLang();
  const t = TX[lang];
  const [rows, setRows] = useState<ActivityRow[] | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [shown, setShown] = useState(PAGE);
  useEffect(() => {
    p.api.get<{ activity: ActivityRow[] }>(`/api/projects/${p.id}/activity`).then((r) => setRows(r.activity), setErr);
  }, [p.id]);
  if (err) return <LoadError error={err} />;
  if (!rows) return <Loading />;
  const kinds = (['step', 'casting', 'change'] as const).filter((k) => rows.some((r) => r.kind === k));
  const days = groupActivity(filter === 'all' ? rows : rows.filter((r) => r.kind === filter));
  // page by entries, keeping whole days' headings
  let left = shown;
  const visible = days
    .map((d) => {
      const entries = d.entries.slice(0, Math.max(0, left));
      left -= d.entries.length;
      return { ...d, entries };
    })
    .filter((d) => d.entries.length);
  const total = days.reduce((n, d) => n + d.entries.length, 0);
  const now = p.now ?? new Date();
  return (
    <Block
      title={t.activity}
      actions={
        kinds.length > 1 ? (
          <div class="chips" role="group" aria-label={t.show}>
            {(['all', ...kinds] as Filter[]).map((f) => (
              <button key={f} type="button" class="chip" aria-pressed={filter === f} onClick={() => (setFilter(f), setShown(PAGE))}>
                {t.filters[f]}
              </button>
            ))}
          </div>
        ) : undefined
      }
    >
      {visible.length ? (
        <div class="pa">
          {visible.map((d) => (
            <section key={d.key} class="pa-day" aria-label={dayLabel(d.key, now, lang)}>
              <h3 class="pa-date">{dayLabel(d.key, now, lang)}</h3>
              <ul class="pa-list">
                {d.entries.map((e) => (
                  <EntryRow key={`${e.at}-${e.actor}`} e={e} id={p.id} lang={lang} />
                ))}
              </ul>
            </section>
          ))}
          {total > shown ? (
            <button type="button" class="btn btn-sm pa-more" onClick={() => setShown(shown + PAGE)}>
              {t.older}
            </button>
          ) : null}
        </div>
      ) : (
        <p class="muted">{t.nothingYet}</p>
      )}
    </Block>
  );
}

function EntryRow(p: { e: Entry; id: string; lang: Lang }) {
  const { e } = p;
  const t = TX[p.lang];
  const first = e.rows[0]!;
  const role = first.kind === 'casting' ? first.casting?.role : null;
  const label = [e.actor, e.actorRole].filter(Boolean).join(' · ');
  return (
    <li class="pa-row">
      <time class="pa-time" dateTime={e.at}>
        {clock(e.at)}
      </time>
      {e.actor && !e.system ? (
        <Avatar name={e.actor} size="sm" label={label} />
      ) : (
        <span class="avatar avatar--sm avatar--more" title={e.actor ?? undefined} aria-label={e.actor ?? undefined} role={e.actor ? 'img' : undefined}>
          {e.system ? 'M' : '·'}
        </span>
      )}
      <div class="pa-body">
        {first.kind === 'casting' ? (
          <div class="pa-ctx">
            {t.casting}
            {role ? (
              <>
                {' · '}
                <RoleLink id={p.id} role={role} lang={p.lang} />
              </>
            ) : null}
            {e.rows.length > 1 ? ` · ${t.changes(e.rows.length)}` : ''}
          </div>
        ) : null}
        {e.rows.map((r, i) => (
          <div key={i} class="pa-line">
            <Line r={r} id={p.id} lang={p.lang} />
          </div>
        ))}
      </div>
    </li>
  );
}

function RoleLink(p: { id: string; role: Ref; lang: Lang }) {
  if (!p.role.id) return <>{p.role.name}</>;
  const href = `/projects/${p.id}/actors#role-${p.role.id}`;
  return (
    <a class="pa-link" href={href} onClick={go(href)} title={TX[p.lang].openRole(p.role.name)}>
      {p.role.name}
    </a>
  );
}

function Person(p: { x: Ref; lang: Lang }) {
  if (!p.x.id) return <strong>{p.x.name}</strong>;
  const href = `/actors?q=${encodeURIComponent(p.x.name)}`;
  return (
    <a class="pa-link pa-strong" href={href} onClick={go(href)} title={TX[p.lang].openPerson(p.x.name)}>
      {p.x.name}
    </a>
  );
}

function People(p: { xs: Ref[]; lang: Lang }) {
  const out: ComponentChildren[] = [];
  p.xs.forEach((x, i) => {
    if (i) out.push(i === p.xs.length - 1 ? ` ${TX[p.lang].and} ` : ', ');
    out.push(<Person key={i} x={x} lang={p.lang} />);
  });
  return <>{out.length ? out : TX[p.lang].aCandidate}</>;
}

function Line(p: { r: ActivityRow; id: string; lang: Lang }) {
  const { r } = p;
  const t = TX[p.lang];
  if (r.kind === 'step' && r.video && r.to) {
    const href = `/projects/${p.id}#video-${r.video.id}`;
    return (
      <>
        {r.video.id ? (
          <a class="pa-link pa-strong" href={href} onClick={go(href)} title={t.openVideo(r.video.name)}>
            {r.video.name}
          </a>
        ) : (
          <strong>{r.video.name}</strong>
        )}{' '}
        {r.from ? (
          <>
            <StageText s={r.from} /> <span class="pa-arrow" aria-hidden="true">→</span>{' '}
          </>
        ) : null}
        <StageText s={r.to} />
        {r.note?.length ? <span class="pa-note"> · {r.note.join(' · ')}</span> : null}
      </>
    );
  }
  const c = r.casting;
  if (r.kind !== 'casting' || !c) return <>{r.text}</>;
  const chip = c.status ? <Pill tone={TONE[c.status]}>{t.status[c.status]}</Pill> : null;
  switch (c.verb) {
    case 'role_added':
      return <>{t.roleAdded}</>;
    case 'role_changed':
      return <>{t.roleChanged}</>;
    case 'role_removed':
      return <>{t.roleRemoved}</>;
    case 'candidates_added':
      return (
        <>
          {t.add} <People xs={c.people} lang={p.lang} /> {t.added(c.people.length)}
        </>
      );
    case 'candidate_removed':
      return (
        <>
          {t.tookOff} <People xs={c.people} lang={p.lang} />
        </>
      );
    case 'booked':
      return (
        <>
          {t.booked} <People xs={c.people} lang={p.lang} /> {chip}
        </>
      );
    case 'asked':
      return (
        <>
          {t.asked(c.count ?? 0)}
          <span class="pa-note"> · {c.emailed ? t.emailed(c.emailed) : t.copied}</span>
        </>
      );
    case 'answered':
      return (
        <>
          <People xs={c.people} lang={p.lang} /> {t.answered} {chip}
          {c.tape ? <span class="pa-note"> · {t.tape}</span> : null}
        </>
      );
    default:
      return (
        <>
          <People xs={c.people} lang={p.lang} /> {chip}
        </>
      );
  }
}

function StageText(p: { s: { stage: string; label: string } }) {
  const cls = STEP_CLASS[p.s.stage];
  return (
    <span class={`pa-stage${cls ? ` st-${cls}` : ''}`}>
      <span class="st-dot" aria-hidden="true" />
      {p.s.label}
    </span>
  );
}

/** After a link from the log: bring the video card or casting role named in the address's #hash into view, briefly marked. */
export function useHashFocus(ready: boolean) {
  useEffect(() => {
    if (!ready || !location.hash) return;
    const el = document.getElementById(decodeURIComponent(location.hash.slice(1)));
    if (!el) return;
    // after the page's own scroll to the top on a new address
    const t = setTimeout(() => {
      el.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      el.classList.add('is-flash');
    }, 60);
    const off = setTimeout(() => el.classList.remove('is-flash'), 2400);
    return () => (clearTimeout(t), clearTimeout(off));
  }, [ready]);
}
